import { useState } from "react";

import {
  RATE_LIMIT_MOVED_KEYS,
  settingsParamsOf,
  writableKeysOf,
  type McpSettings as McpSettingsData,
} from "../../api/mcp";
import { SETTING_RULES, validateSetting } from "../../api/settingsRules";
import { useAccess } from "../../auth/useAccess";
import { Gate, ForbiddenNotice } from "../../components/authority/Gate";
import { EditableSetting } from "../../components/settings/EditableSetting";
import { RateLimitEditor } from "../../components/settings/RateLimitEditor";
import { Breadcrumbs } from "../../components/Layout";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Card, Notice, Surface, Panel } from "../../components/ui";
import { ConfirmDialog } from "../../components/write/ConfirmDialog";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { Button } from "../../f7";

// No "Rate limits" entry here on purpose. MCP moved those four keys off
// `PATCH /admin/settings` entirely onto their own paired route — they are
// `writable: false` on this response now, so they never match a row in the
// writable loop below. `RateLimitEditor` below owns them; see
// RATE_LIMIT_MOVED_KEYS in api/mcp.ts for the read-only-here explanation.
const CATEGORIES: { title: string; note: string; match: (k: string) => boolean }[] = [
  {
    title: "Messages",
    note: "What a merchant sees when refused. Live only after Apply, or the next restart.",
    match: (k) => k.endsWith("_message"),
  },
  {
    title: "Flag",
    note: "The one key re-read on a TTL — live within ~60 seconds, no restart needed.",
    match: (k) => k === "audit/gate_rows_enabled",
  },
  {
    title: "Response",
    note: "Live only after Apply, or the next restart.",
    match: (k) => k.startsWith("response/"),
  },
];

interface SettingsResponse {
  applied?: Record<string, { value: string; effect: string }>;
  staged?: string[];
  note?: string;
}

interface ApplyResponse {
  cluster?: string;
  service?: string;
  deployment_id?: string;
  status?: string;
  note?: string;
}

/**
 * `embedded` renders the body WITHOUT its own <Page> heading, for when this is
 * a panel inside another surface rather than a tab of its own. The console's
 * nav is exactly the twelve tabs the design draws, so several real surfaces
 * live inside another one — see nav/sections.ts.
 */
export function Settings({ config, embedded = false }: { config: AppConfig; embedded?: boolean }) {
  const { state, reload } = useResource<McpSettingsData>(config, "/mcp/admin/settings");
  const access = useAccess();
  const write = useWrite<SettingsResponse>(config);
  const apply = useWrite<ApplyResponse>(config);

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  // Held in memory only, like the token — a reload loses it. See the note in
  // the banner: GET /admin/settings has no `staged` field to rebuild it from.
  const [staged, setStaged] = useState<string[]>([]);
  const [applied, setApplied] = useState<ApplyResponse | null>(null);

  const isProd = config.env === "prod";

  const dirty = Object.entries(edits);
  const errors = dirty
    .map(([k, v]) => [k, validateSetting(k, v)] as const)
    .filter(([, e]) => e !== null);

  function onSave() {
    // PATCH /admin/settings is `{ values }` only (`extra=forbid`). A typed
    // reason would 422. MCP writes one audit row per key with that key's
    // before/after — that is the trail, not a reason field.
    const values: Record<string, string | number | boolean> = {};
    for (const [k, v] of dirty) {
      const rule = SETTING_RULES[k];
      values[k] =
        rule?.kind === "number" ? Number(v) : rule?.kind === "boolean" ? v === "true" : v;
    }
    void write
      .submit("/mcp/admin/settings", { method: "PATCH", body: JSON.stringify({ values }) })
      .then((r) => {
        if (!r) return;
        setStaged((prev) => [...new Set([...prev, ...(r.staged ?? [])])]);
        setEdits({});
        setConfirming(false);
        reload();
      });
  }

  function onApply() {
    // `confirm` is constructed HERE, never as a form default. The API defaults
    // it to false precisely so a restart cannot fire by omission.
    void apply
      .submit("/mcp/admin/settings/apply", {
        method: "POST",
        body: JSON.stringify({ confirm: true }),
      })
      .then((r) => {
        if (!r) return;
        setApplied(r);
        setStaged([]);
        setApplying(false);
      });
  }

  return (
    <Surface embedded={embedded} title="Settings" subtitle="MCP's runtime configuration.">
      {/* Only when this is its own page. Embedded under Feature flags it
          rendered "Overview › Settings" inside another surface, naming a
          location the reader is not at. */}
      {!embedded && (
        <Breadcrumbs trail={[{ label: "Overview", to: "/" }, { label: "Settings" }]} />
      )}

      {state.kind === "loading" && <Loading what="settings" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="settings" message={state.error.message} onRetry={reload} />
      )}
      {state.kind === "notfound" && <LoadFailed what="settings" message="Route not found." />}
      {state.kind === "error" && <LoadFailed what="settings" message={state.error.message} />}

      {state.kind === "ready" &&
        (() => {
          const s = state.data;
          const writable = new Set(writableKeysOf(s));
          const params = settingsParamsOf(s);
          const readOnly = params.filter((p) => !writable.has(p.key));

          return (
            <>
              {/* Excludes audit/gate_rows_enabled — it goes live in ~60s, and
                  listing it would train people to ignore the banner. */}
              {staged.filter((k) => k !== "audit/gate_rows_enabled").length > 0 && (
                <div className="mb-4">
                  <Notice tone="amber" title="Changed, but not yet live">
                    <p>
                      {staged
                        .filter((k) => k !== "audit/gate_rows_enabled")
                        .map((k) => (
                          <span key={k} className="ref mr-2 text-xs">
                            {k}
                          </span>
                        ))}
                    </p>
                    <p className="mt-1 text-xs">
                      These reach running tasks only after Apply, or the next restart.
                      This list lives in the page — a reload will lose it.
                    </p>
                  </Notice>
                </div>
              )}

              <Notice title="When a change takes effect">
                Most of these reach running tasks only when the service is restarted —{" "}
                <span className="ref">audit/gate_rows_enabled</span> is the one exception,
                re-read on a ~60 second TTL. Each row states its own timing.
              </Notice>

              {write.state.kind === "denied" && (
                <div className="mt-4">
                  <ForbiddenNotice message={write.state.error.message} />
                </div>
              )}
              {write.state.kind === "invalid" && (
                <div className="mt-4">
                  <Notice tone="red" title="Refused — nothing was written">
                    {write.state.error.message}
                  </Notice>
                </div>
              )}

              {/* An UNCATEGORISED writable key still gets a group.
                  `CATEGORIES.map` alone rendered only keys matching one of the
                  four prefixes, so any setting MCP adds under a new prefix
                  would be writable on the API and invisible here — the console
                  would silently omit a knob rather than show one it cannot
                  describe. The catch-all carries no note, because there is
                  nothing true to say about a key this page has not been told
                  about; each row still states its own effect. */}
              {[
                ...CATEGORIES.map((c) => ({
                  title: c.title,
                  note: c.note,
                  rows: params.filter((p) => writable.has(p.key) && c.match(p.key)),
                })),
                {
                  title: "Other",
                  note: null,
                  rows: params.filter(
                    (p) => writable.has(p.key) && !CATEGORIES.some((c) => c.match(p.key)),
                  ),
                },
              ]
                .filter((g) => g.rows.length > 0)
                .map((g) => (
                  <Card key={g.title} title={g.title} note={g.note}>
                    <div className="flex flex-col gap-2">
                      {g.rows.map((p) => (
                        <EditableSetting
                          key={p.key}
                          param={p}
                          value={edits[p.key] ?? p.value ?? ""}
                          dirty={p.key in edits}
                          canWrite={access.can("settings:write")}
                          onChange={(v) =>
                            setEdits((e) =>
                              v === (p.value ?? "")
                                ? Object.fromEntries(
                                    Object.entries(e).filter(([k]) => k !== p.key),
                                  )
                                : { ...e, [p.key]: v },
                            )
                          }
                        />
                      ))}
                    </div>
                  </Card>
                ))}

              {/* Its own card, not a fifth CATEGORIES entry: it has its own
                  route (PATCH /admin/ratelimit/{lane}), its own validation
                  (a live normalized-rate check, not a static bound this
                  page's shared save bar could express), and its own Save
                  per lane rather than batching into the page's dirty-count
                  bar below. Two lanes replace the four independent fields
                  the old Settings page rendered for these keys. */}
              <Card
                title="Rate limits"
                note="Two lanes — core and reports — each its own paired route. Moved off Settings entirely."
              >
                <RateLimitEditor config={config} params={params} onDone={reload} />
              </Card>

              {dirty.length > 0 && (
                <div className="sticky bottom-4 mt-6">
                  <Panel className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <p className="text-sm">
                      <span className="font-medium">{dirty.length}</span> change
                      {dirty.length === 1 ? "" : "s"} not saved
                      {errors.length > 0 && (
                        <span className="ml-2 text-red-600 dark:text-red-400">
                          · {errors.length} would be refused
                        </span>
                      )}
                    </p>
                    <div className="flex gap-2">
                      <Button outline onClick={() => setEdits({})}>
                        Discard
                      </Button>
                      <Gate need="settings:write">
                        <Button onClick={() => setConfirming(true)} disabled={errors.length > 0}>
                          Review {dirty.length} change{dirty.length === 1 ? "" : "s"}
                        </Button>
                      </Gate>
                    </div>
                  </Panel>
                </div>
              )}

              <section className="mt-6">
                <details>
                  <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Read-only parameters ({readOnly.length})
                  </summary>
                  <p className="mb-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Changing any of these would repoint storage or break auth and serving,
                    so the API refuses them. Secrets are excluded entirely and never read.
                  </p>
                  <div className="space-y-1">
                    {readOnly.map((p) => (
                      <Panel key={p.key} className="px-4 py-2">
                        <div className="flex flex-wrap items-baseline gap-x-3 text-xs">
                          <span className="ref">{p.key}</span>
                          <span className="ref break-all text-slate-500 dark:text-slate-400">
                            {p.exists ? p.value : "not seeded — code default applies"}
                          </span>
                          {/* These four are read-only HERE specifically —
                              not dead, just moved. Say where, rather than
                              leaving an operator to wonder why a rate limit
                              cannot be edited on the page named Settings. */}
                          {(RATE_LIMIT_MOVED_KEYS as readonly string[]).includes(p.key) && (
                            <span className="text-amber-700 dark:text-amber-400">
                              — edit above, under Rate limits
                            </span>
                          )}
                        </div>
                      </Panel>
                    ))}
                  </div>
                </details>
              </section>

              {/* Its own panel, visually separate from Save. A mis-click on
                  Save stages a value; a mis-click here restarts live traffic. */}
              <section className="mt-8">
                <Panel className="border-l-2 border-l-red p-4">
                  <h2 className="text-sm font-semibold">Apply to the running service</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Rolling restart of the merchant-facing MCP service so tasks pick up new
                    configuration. Not a code deploy — the existing deployment circuit
                    breaker rolls back on failure.
                  </p>
                  <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
                    Apply is not scoped to your changes. It restarts the service, which
                    picks up <strong>every</strong> value changed by anyone since the last
                    restart.
                  </p>

                  {applied && (
                    <div className="mt-3">
                      <Notice tone="amber" title="Restart started">
                        <p>
                          Service <span className="ref">{applied.service}</span>, deployment{" "}
                          <span className="ref">{applied.deployment_id}</span> ({applied.status}).
                        </p>
                        <p className="mt-1 text-xs">
                          Tasks pick up the new values as they come up. There is no way to
                          observe completion from here — this says started, not done.
                        </p>
                      </Notice>
                    </div>
                  )}
                  {apply.state.kind === "denied" && (
                    <div className="mt-3">
                      <ForbiddenNotice message={apply.state.error.message} />
                    </div>
                  )}
                  {apply.state.kind === "unavailable" && (
                    <div className="mt-3">
                      <Notice tone="amber" title="Nothing was restarted">
                        {apply.state.error.message} The ECS cluster name could not be read
                        from SSM.
                      </Notice>
                    </div>
                  )}

                  <div className="mt-3">
                    <Gate need="service:restart">
                      <Button color="red" onClick={() => setApplying(true)}>
                        Apply…
                      </Button>
                    </Gate>
                  </div>
                </Panel>
              </section>

              <ConfirmDialog
                open={confirming}
                title={`Save ${dirty.length} setting${dirty.length === 1 ? "" : "s"}`}
                confirmLabel="Save"
                requireReason={false}
                busy={write.state.kind === "submitting"}
                onCancel={() => setConfirming(false)}
                onConfirm={() => onSave()}
                effect="Saved values are staged. Most reach running tasks only after Apply, or the next restart — audit/gate_rows_enabled is live within ~60 seconds."
              >
                <p>Nothing is written unless every value below is accepted.</p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  MCP records each key&rsquo;s before and after on the audit log. There is no
                  reason field on this route.
                </p>
                <ul className="mt-2 space-y-1">
                  {dirty.map(([k, v]) => (
                    <li key={k} className="text-xs">
                      <span className="ref">{k}</span>
                      <div className="ref mt-0.5 break-all text-text">{v}</div>
                    </li>
                  ))}
                </ul>
              </ConfirmDialog>

              <ConfirmDialog
                open={applying}
                title="Restart the merchant-facing service"
                confirmLabel={isProd ? "Restart production" : "Restart"}
                destructive
                // L3 in prod only. Three type-to-confirm cases exist in the
                // whole console; if everything needed one, none would matter.
                typeToConfirm={isProd ? "foodics-mcp-foodics-prod" : undefined}
                busy={apply.state.kind === "submitting"}
                onCancel={() => setApplying(false)}
                onConfirm={onApply}
                effect="Tasks restart in a rolling deployment; the circuit breaker rolls back on failure. There is no way to observe completion from this console."
              >
                <p>
                  This restarts the live merchant-facing MCP service
                  {isProd && (
                    <>
                      {" "}
                      <strong className="text-red-600 dark:text-red-400">in production — real merchants</strong>
                    </>
                  )}
                  .
                </p>
                <p>
                  It picks up <strong>every</strong> configuration value changed since the
                  last restart, not only yours.
                </p>
                {staged.length === 0 && (
                  <p className="text-amber-700 dark:text-amber-400">
                    You have nothing staged — but that does not make this a no-op. Anything
                    another person changed will go live too.
                  </p>
                )}
              </ConfirmDialog>
            </>
          );
        })()}
    </Surface>
  );
}
